import React from 'react';
import { Box, Button, FormControl, Grid, Modal, TextField, Typography } from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { LoadingButton } from '@mui/lab';
import { makeStyles } from '@mui/styles';
import { ResolveNotification } from '../../../network/Stats';
import { getNotificationEventStatus } from '../../../utils/Utilities';

const useStyles = makeStyles({
  textStyle: {
    fontSize: '12px',
    fontWeight: '600',
    lineHeight: '18px',
    textAlign: 'left',
    color: '#7c7d7d',
    whiteSpace: 'nowrap'
  },
  spanStyle: {
    color: '#171D40',
    fontWeight: '700',
    textTransform: 'uppercase'
  }
});

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: { xs: '70%', md: '40%' },
  maxWidth: { xs: '70%', md: '40%' },
  bgcolor: 'background.paper',
  p: 2,
  borderRadius: 1,
  minHeight: '30vh',
  maxHeight: '90vh',
  overflowY: 'auto'
};

export default function MarkResolvedModal({
  openAssignedModal,
  handleCloseModal,
  setAlertMetas,
  alertMetas,
  selectedAlert,
  updatedNotification
}) {
  const classes = useStyles();

  const commentSchema = Yup.object().shape({
    comment: Yup.string().required('Comment is required')
  });

  const handleFormSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    const payload = {
      lockId: selectedAlert.lock_id,
      eventType: selectedAlert?.event_type,
      description: values.comment
    };

    try {
      const res = await ResolveNotification(payload);
      if (res) {
        setAlertMetas({
          ...alertMetas,
          severity: 'success',
          snackBarOpened: true,
          msg: 'Notification Resolved!'
        });
        updatedNotification();
        handleCloseModal();
        setTimeout(() => {
          setAlertMetas({ ...alertMetas, snackBarOpened: false, msg: '' });
        }, 4000);
      }
    } catch (error) {
      setAlertMetas({
        ...alertMetas,
        severity: 'error',
        snackBarOpened: true,
        msg: error?.response?.data?.message || 'An error occurred'
      });
      setTimeout(() => {
        setAlertMetas({ ...alertMetas, snackBarOpened: false, msg: '' });
      }, 4000);
    } finally {
      setSubmitting(false);
      resetForm();
    }
  };

  return (
    <Modal
      open={openAssignedModal}
      onClose={handleCloseModal}
      aria-labelledby="resolve-issue-modal-title"
      aria-describedby="resolve-issue-modal-description"
      data-testid="mark-resolved-modal"
    >
      <Box sx={style}>
        <Typography sx={{ fontSize: { xs: 16, sm: 18 }, fontWeight: 900, mb: 2 }}>
          Resolve Issue
        </Typography>
        <Formik
          initialValues={{ comment: '' }}
          validationSchema={commentSchema}
          onSubmit={handleFormSubmit}
        >
          {({ errors, touched, getFieldProps, isSubmitting }) => (
            <Form autoComplete="off" noValidate>
              <Grid container sx={{ gap: { xs: 1, lg: 2 }, marginBottom: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography className={classes.textStyle}>
                    Device ID:{' '}
                    <span className={classes.spanStyle}>{selectedAlert?.device_name}</span>
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography className={classes.textStyle}>
                    IMEI:{' '}
                    <span className={classes.spanStyle}>
                      {getNotificationEventStatus(selectedAlert?.event_type)}
                    </span>
                  </Typography>
                </Grid>
              </Grid>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <TextField
                  label={
                    <Typography sx={{ fontSize: { xs: '12px', sm: '14px' }, fontWeight: 500 }}>
                      Comment *
                    </Typography>
                  }
                  id="comment"
                  name="comment"
                  sx={{
                    '& .MuiOutlinedInput-input': {
                      fontSize: { xs: '12px', sm: '14px' },
                      padding: { xs: '14px 8px', sm: '16.5px' }
                    }
                  }}
                  variant="outlined"
                  {...getFieldProps('comment')}
                  error={Boolean(touched.comment && errors.comment)}
                  helperText={touched.comment && errors.comment}
                />
              </FormControl>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', mt: 1 }}>
                <Button variant="outlined" size="small" onClick={handleCloseModal}>
                  Cancel
                </Button>
                <LoadingButton
                  variant="contained"
                  type="submit"
                  size="small"
                  loading={isSubmitting}
                >
                  Confirm
                </LoadingButton>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
}
